import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import Dropdown from '../inputs/dropdown/Dropdown';
import Button from '../button/Button';
import DatePicker from '../inputs/date-picker/DatePicker';
import TextInput from '../inputs/text-input/TextInput';

import './product-filter.scss';

const ProductFilter = ({
  classifications,
  categories,
  types,
  retrieveCategories,
  retrieveTypes,
  onApplyFiltersClick,
  currencyCode,
}) => {
  const [productClassification, setProductClassification] = useState(undefined);
  const [productCategory, setProductCategory] = useState(undefined);
  const [productType, setProductType] = useState(undefined);
  const [minimumPrice, setMinimumPrice] = useState('');
  const [maximumPrice, setMaximumPrice] = useState('');
  const [offerFrom, setOfferFrom] = useState(undefined);
  const [offerTo, setOfferTo] = useState(undefined);
  const [listingFrom, setListingFrom] = useState(undefined);
  const [listingTo, setListingTo] = useState(undefined);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const ref = useRef(null);

  useEffect(() => {
    if (filtersApplied && ref.current) {
      ref.current.scrollTo(0, ref.current.scrollHeight);
    }
  }, [filtersApplied]);

  useEffect(() => {
    setProductClassification(undefined);
    setProductCategory(undefined);
    setProductType(undefined);
    setMinimumPrice('');
    setMaximumPrice('');
    setOfferFrom(undefined);
    setOfferTo(undefined);
    setListingFrom(undefined);
    setListingTo(undefined);
    setFiltersApplied(false);
  }, [currencyCode]);

  const handleClassificationChange = (classification) => {
    setProductClassification(classification);
    setProductCategory(null);
    setProductType(null);
    retrieveCategories(classification.externalReference);
  };

  const handleCategoryChange = (category) => {
    setProductCategory(category);
    setProductType(null);
    retrieveTypes(category.externalReference);
  };

  const handleTypeChange = (type) => {
    setProductType(type);
  };

  const handleMinPriceChange = (minPrice) => {
    setMinimumPrice(minPrice.target.value);
  };

  const handleMaxPriceChange = (maxPrice) => {
    setMaximumPrice(maxPrice.target.value);
  };

  const handleApplyFiltersClick = () => {
    const filters = {
      productClassificationExternalReference: productClassification?.externalReference,
      productCategoryExternalReference: productCategory?.externalReference,
      productTypeExternalReference: productType?.externalReference,
      minimumPrice,
      maximumPrice,
      offerFrom,
      offerTo,
      listingFrom,
      listingTo,
    };

    if (haveFiltersChanged()) {
      setFiltersApplied(true);
    } else {
      setFiltersApplied(false);
    }
    onApplyFiltersClick(filters);
  };

  const haveFiltersChanged = () => {
    if (
      productClassification ||
      productCategory ||
      productType ||
      minimumPrice ||
      maximumPrice ||
      offerFrom ||
      offerTo ||
      listingFrom ||
      listingTo
    ) {
      return true;
    }

    return false;
  };

  const removeFilters = () => {
    setProductClassification(undefined);
    setProductCategory(undefined);
    setProductType(undefined);
    setMinimumPrice('');
    setMaximumPrice('');
    setOfferFrom(null);
    setOfferTo(null);
    setListingFrom(null);
    setListingTo(null);

    setFiltersApplied(false);
    onApplyFiltersClick({});
  };

  return (
    <section className="product-filter" ref={ref}>
      <section className="product-filter__filters">
        <p>Filter</p>

        <label htmlFor="product-classification">Product Classification</label>
        <Dropdown
          name="product-classification"
          value={productClassification}
          options={classifications}
          onChange={handleClassificationChange}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
        />

        <label htmlFor="product-category">Product Category</label>
        <Dropdown
          name="product-category"
          value={productCategory}
          options={categories}
          onChange={handleCategoryChange}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
          disabled={!productClassification}
        />

        <label htmlFor="product-type">Product Type</label>
        <Dropdown
          name="product-type"
          value={productType}
          options={types}
          onChange={handleTypeChange}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
          disabled={!productCategory}
        />

        <p>Price per Product Unit</p>
        <div className="product-filter__price-inputs">
          <div>
            <label htmlFor="min-price">Minimum Price:</label>
            <TextInput name="min-price" onChange={handleMinPriceChange} value={minimumPrice} numberOnly={true} />
          </div>
          <div>
            <label htmlFor="max-price">Maximum Price:</label>
            <TextInput name="max-price" onChange={handleMaxPriceChange} value={maximumPrice} numberOnly={true} />
          </div>
        </div>

        <p>Offer Availability</p>
        <div className="product-filter__date-pickers">
          <div>
            <label htmlFor="offer-from">From</label>
            <DatePicker name="offer-from" value={offerFrom} onChange={(date) => setOfferFrom(date)} maxDate={offerTo} />
          </div>
          <div>
            <label htmlFor="offer-to">To</label>
            <DatePicker name="offer-to" value={offerTo} onChange={(date) => setOfferTo(date)} minDate={offerFrom} />
          </div>
        </div>

        <p>Listing Availability</p>
        <div className="product-filter__date-pickers">
          <div>
            <label htmlFor="listing-from">From</label>
            <DatePicker
              name="listing-from"
              value={listingFrom}
              onChange={(date) => setListingFrom(date)}
              maxDate={listingTo}
            />
          </div>
          <div>
            <label htmlFor="listing-to">To</label>
            <DatePicker
              name="listing-to"
              value={listingTo}
              onChange={(date) => setListingTo(date)}
              minDate={listingFrom}
            />
          </div>
        </div>
      </section>

      <div className="product-filter__button-container">
        <Button onClick={handleApplyFiltersClick}>Apply Filter</Button>
        {filtersApplied && <Button onClick={() => removeFilters()}>Remove Filter</Button>}
      </div>
    </section>
  );
};

ProductFilter.propTypes = {
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),
  metricUnits: PropTypes.arrayOf(PropTypes.shape({})),
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  onApplyFiltersClick: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

ProductFilter.defaultProps = {
  classifications: [],
  categories: [],
  types: [],
  metricUnits: [],
};

export default ProductFilter;
